/**
 * LeatherMirrorTool — the leather "Mirror" method (#973, epic #781): with a
 * part, cutout or seam selected, hover an axis on any visible outline and click
 * to drop a standalone mirrored duplicate.
 *
 * The tool itself is a thin shell. It OWNS a `LeatherMirrorController` and
 * drives it by composition — the same arrangement `OutlineEditTool` uses with
 * `OutlineEditController` — so the whole gesture (axis scan, preview, commit)
 * lives in the controller and this class only supplies editor plumbing and the
 * pointer wiring.
 *
 * Mirror is selection-driven and mode-independent: unlike Draw / Trace / Fill /
 * Edit it doesn't care whether the user was last working in Leather, Cutout or
 * Stitch, because what gets mirrored is whatever is selected. `app.ts` hides
 * the mode sub-sub-tools while it's active for exactly that reason.
 */
import type paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene, SceneSelection } from "../canvas/Scene";
import type { Project } from "../model/Project";
import { setCanvasCursor } from "../canvas/canvasCursor";
import {
  LeatherMirrorController,
  type LeatherMirrorHost,
} from "./leatherMirrorController";

export class LeatherMirrorTool extends ToolBase implements LeatherMirrorHost {
  id = "leatherMirror";
  label = "Mirror";
  // A plain pointer, not a crosshair: this gesture grabs an existing axis
  // rather than placing a point — the same reasoning the Offset kind and the
  // construction Mirror sub-tool use.
  cursor = "default";

  private mirror = new LeatherMirrorController(this);

  // --- LeatherMirrorHost ---------------------------------------------------

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }

  getScene(): Scene {
    return this.ctx.scene;
  }

  getProject(): Project {
    return this.ctx.history.state;
  }

  commit(label: string, mutate: (proj: Project) => Project): void {
    this.ctx.commit(label, mutate);
  }

  setSelection(sel: SceneSelection): void {
    this.ctx.setSelection(sel);
  }

  activateTool(id: string): void {
    this.ctx.activateTool(id);
  }

  // --- Pointer flow --------------------------------------------------------

  onActivate(): void {
    setCanvasCursor(this.getScope(), this.cursor);
  }

  onDeactivate(): void {
    this.mirror.clear();
  }

  onHover(s: PointerSample, p: { x: number; y: number }): void {
    this.mirror.handleHover(p, s.pointerType);
  }

  onDown(s: PointerSample, p: { x: number; y: number }): void {
    this.mirror.handleDown(p, s.pointerType);
  }

  /** A finger and a stylus have no hover, so mirroring is a DRAG for them
   *  (#40): the press previews, this tracks, and the release commits. A mouse
   *  hovered first and committed on the press, so both of these are no-ops. */
  onMove(s: PointerSample, p: { x: number; y: number }): void {
    this.mirror.handleMove(p, s.pointerType);
  }

  onUp(s: PointerSample, p: { x: number; y: number }): void {
    this.mirror.handleUp(p, s.pointerType);
  }

  onCancel(): void {
    this.mirror.clear();
  }

  /** Test hook (#973): the axis ref currently under the cursor, or null.
   *  Surfaced through `__editor` so e2e can assert what the cursor resolves to
   *  without reading overlay items. */
  getHoverAxis() {
    return this.mirror.getHoverAxis();
  }

  /** Test hook (#973): bounds of the dashed reflection preview, so e2e can
   *  check the preview and the commit land in the same place. */
  getPreviewBounds() {
    return this.mirror.getPreviewBounds();
  }

  // --- Draft contract (✓ / ✗ fly-out + Enter / Escape) ---------------------
  //
  // Mirror's "draft" is the armed reflection under the cursor rather than a
  // click-built point list, but it presents the same contract every other draft
  // tool does, so it drops straight into the shared `makeDraftFlyout` builder
  // and the shared key handling.

  /** 1 while a mirror is armed, else 0. */
  get vertexCount(): number {
    return this.mirror.draftCount;
  }

  setDraftChangeListener(fn: () => void): void {
    this.mirror.setDraftChangeListener(fn);
  }

  /** Enter / ✓ — commit the armed mirror. False when nothing is armed. */
  handleFinish(): boolean {
    return this.mirror.handleFinish();
  }

  /** Escape / ✗ — disarm and drop the source selection. */
  handleDiscard(): boolean {
    return this.mirror.handleDiscard();
  }
}
